import { __assign, __read, __rest } from "tslib";
import React, { useState, useImperativeHandle, useEffect, forwardRef, useRef, } from "react";
import classNames from "classnames";
import ReactDOM, { unmountComponentAtNode, createPortal } from "react-dom";
import { useConfig } from "../_util/config-context";
import { Button } from "../button";
import { Justify } from "../justify";
import { Icon } from "../icon";
import { Text } from "../text";
import { ConfigProvider } from "../configprovider";
import { useVisibleTransition } from "../_util/use-visible-transition";
import { callBoth } from "../_util/call-both";
import { noop } from "../_util/noop";
import { getRoot, NotificationTransition, limit } from "./util";
import { getPlainText } from "../_util/getPlainText";
import { useIsomorphicLayoutEffect } from "../_util/use-isomorphic-layout-effect";
import { useTranslation } from "../i18n";
var themeMap = {
    // type: [icon, theme]
    success: ["success", "success"],
    warning: ["warning", "warning"],
    error: ["error", "danger"],
};
var Notification = forwardRef(function NotificationShow(_a, ref) {
    var _b;
    var type = _a.type, title = _a.title, extra = _a.extra, description = _a.description, footer = _a.footer, _c = _a.duration, duration = _c === void 0 ? 5000 : _c, className = _a.className, style = _a.style, _d = _a.onClose, onClose = _d === void 0 ? noop : _d, _e = _a.onClick, onClick = _e === void 0 ? noop : _e, onFooterClick = _a.onFooterClick;
    var classPrefix = useConfig().classPrefix;
    var t = useTranslation();
    var _f = __read(useState(false), 2), visible = _f[0], setVisible = _f[1];
    var timerRef = useRef(null);
    useImperativeHandle(ref, function () { return ({
        setVisible: setVisible,
        renew: function () {
            clearTimeout(timerRef.current);
            if (visible && duration !== 0) {
                timerRef.current = setTimeout(function () { return setVisible(false); }, duration);
            }
        },
    }); });
    // 渲染之后，马上显示
    useEffect(function () {
        setVisible(true);
        return function () { return clearTimeout(timerRef.current); };
    }, []);
    useIsomorphicLayoutEffect(function () {
        clearTimeout(timerRef.current);
        if (visible && duration !== 0) {
            timerRef.current = setTimeout(function () { return setVisible(false); }, duration);
        }
    }, [duration, visible]);
    var _g = useVisibleTransition(visible), shouldContentEnter = _g.shouldContentEnter, shouldContentRender = _g.shouldContentRender, onContentExit = _g.onContentExit;
    if (!shouldContentRender) {
        return null;
    }
    var hasHeader = !!title || !!extra;
    var _h = __read(themeMap[type] || themeMap.success, 2), icon = _h[0], theme = _h[1];
    var content = (React.createElement(NotificationTransition, { in: shouldContentEnter, onExited: callBoth(onClose, onContentExit) },
        React.createElement("div", { className: classNames(classPrefix + "-notification", classPrefix + "-notification--" + type, className), style: style, onMouseEnter: function () { return clearTimeout(timerRef.current); }, onMouseLeave: function () {
                if (duration !== 0) {
                    timerRef.current = setTimeout(function () { return setVisible(false); }, duration);
                }
            }, onClick: onClick },
            React.createElement("div", { className: classPrefix + "-notification__close" },
                React.createElement(Button, { type: "icon", icon: "close", htmlType: "button", onClick: function () {
                        setVisible(false);
                    }, "aria-label": t.closeText })),
            hasHeader && (React.createElement("div", { className: classPrefix + "-notification__header" },
                React.createElement(Justify, { left: React.createElement("div", { className: classPrefix + "-notification__header-title" }, title), right: React.createElement("div", { className: classPrefix + "-notification__header-sub-info" }, extra) }))),
            React.createElement("div", { className: classPrefix + "-notification__body" },
                React.createElement(Icon, { type: icon }),
                React.createElement(Text, { theme: theme, className: classPrefix + "-notification__text", parent: "div" }, description)),
            !!footer && (React.createElement("div", { className: classNames(classPrefix + "-notification__footer", (_b = {},
                    _b[classPrefix + "-notification__footer--link"] = onFooterClick,
                    _b)), onClick: onFooterClick || noop }, onFooterClick ? (React.createElement(Justify, { left: React.createElement(Text, { theme: "label", className: classPrefix + "-fz-reset", parent: "div" }, footer), right: React.createElement(Icon, { type: "arrowright--line" }) })) : (footer))))));
    return createPortal(content, getRoot(classPrefix));
});
Notification.displayName = "Notification";
var showMethod;
/**
 * API 的方式显示通知
 */
function show(type, _a) {
    if (type === void 0) { type = "success"; }
    if (_a === void 0) { _a = {}; }
    var _b = _a.onClose, onClose = _b === void 0 ? noop : _b, _c = _a.onExited, onExited = _c === void 0 ? noop : _c, options = __rest(_a, ["onClose", "onExited"]);
    if (typeof showMethod === "function") {
        return showMethod(type, options);
    }
    var el = document.createElement("div");
    var instanceRef = React.createRef();
    var show = function () {
        ReactDOM.render(React.createElement(ConfigProvider, null,
            React.createElement(Notification, __assign({}, options, { type: type, ref: instanceRef, onClose: function () {
                    onClose();
                    onExited();
                    unmountComponentAtNode(el);
                } }))), el);
    };
    var hide = function () {
        if (instanceRef.current) {
            instanceRef.current.setVisible(false);
        }
    };
    var renew = function () {
        if (instanceRef.current) {
            instanceRef.current.renew();
        }
    };
    var handle = {
        hide: hide,
        renew: renew,
        destroy: hide,
    };
    limit({
        show: show,
        hide: hide,
        renew: renew,
        isExisted: function () { return !!instanceRef.current; },
        identifier: options.unique
            ? [
                type,
                getPlainText(options.title),
                getPlainText(options.description),
                getPlainText(options.extra),
                getPlainText(options.footer),
            ].join("$")
            : null,
    });
    return handle;
}
export var notification = {
    success: function (options) { return show("success", options); },
    warning: function (options) { return show("warning", options); },
    error: function (options) { return show("error", options); },
};
// eslint-disable-next-line dot-notation
notification["setShowMethod"] = function (method) {
    showMethod = method;
};
//# sourceMappingURL=Notification.js.map